import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';

/**
 * SoftReloadService — Safely resets Angular app state without a full page reload.
 *
 * Strategy:
 * 1. Navigate to a neutral blank route (triggers OnDestroy on all active components)
 * 2. Wait 500ms for Angular to run all cleanup hooks
 * 3. Navigate back to the root route (re-initializes all lazy-loaded modules)
 * 4. Issue a GC hint to encourage V8 to collect freed memory
 *
 * When to use:
 * - After long idle periods (triggered by IdleDetectionService)
 * - When MemoryWatchdogService crosses the critical heap threshold
 * - After AutoCleanupService completes a cycle
 * - On a time-based schedule (e.g., every 6h of uptime)
 *
 * Hard reload fallback:
 * - Used when uptime exceeds HARD_RELOAD_THRESHOLD_MS (default: 6 hours)
 * - Triggers window.location.reload() via Android bridge if available
 */
@Injectable({ providedIn: 'root' })
export class SoftReloadService {
  /** Emits just before a soft reload begins */
  readonly beforeReload$ = new Subject<'soft' | 'hard'>();

  /** Emits after a soft reload completes successfully */
  readonly afterReload$ = new Subject<'soft' | 'hard'>();

  private isReloading = false;
  private readonly appStartTime = Date.now();

  /** Uptime threshold after which smartReload() switches to hard reload */
  private readonly HARD_RELOAD_THRESHOLD_MS = 6 * 60 * 60 * 1000; // 6 hours

  constructor(private readonly router: Router) {}

  /**
   * Perform a soft reload:
   * Navigate away → wait → navigate back to the same URL → GC hint.
   * Does NOT reload the page — safe for WebView sessions.
   */
  async softReload(): Promise<void> {
    if (this.isReloading) {
      console.warn('[SoftReload] Reload already in progress — skipping');
      return;
    }

    this.isReloading = true;
    this.beforeReload$.next('soft');
    console.log('[SoftReload] Initiating soft reload...');

    try {
      const targetUrl = this.router.url || '/';

      // Step 1: Navigate away from current route to trigger component OnDestroy
      await this.router.navigate(['/'], { skipLocationChange: true });

      // Step 2: Give Angular time to run all destroy hooks and GC subscriptions
      await this.delay(600);

      // Step 3: Return to the route that was active before soft reload started
      await this.router.navigateByUrl(targetUrl, { replaceUrl: true });

      // Step 4: GC pressure hint (no guarantee, but helps V8)
      this.gcHint();

      console.log('[SoftReload] Soft reload complete');
      this.afterReload$.next('soft');
    } catch (err) {
      console.error('[SoftReload] Error during soft reload:', err);
    } finally {
      this.isReloading = false;
    }
  }

  /**
   * Hard reload — full page reload.
   * Will disconnect any WebSocket / SignalR connections.
   * Prefer softReload() for routine maintenance.
   */
  hardReload(): void {
    this.beforeReload$.next('hard');
    console.log('[SoftReload] Hard reload triggered');

    // Notify Android if bridge is available
    this.notifyAndroid();

    window.location.reload();
  }

  /**
   * Smart reload — automatically selects soft or hard based on uptime.
   * Use this as the default reload strategy.
   */
  smartReload(): void {
    const uptimeMs = Date.now() - this.appStartTime;

    if (uptimeMs >= this.HARD_RELOAD_THRESHOLD_MS) {
      console.log(
        `[SoftReload] Uptime ${Math.round(uptimeMs / 3_600_000)}h exceeds threshold — hard reload`
      );
      this.hardReload();
    } else {
      this.softReload();
    }
  }

  /** Get app uptime in milliseconds */
  get uptimeMs(): number {
    return Date.now() - this.appStartTime;
  }

  /** Get formatted uptime string */
  get uptimeFormatted(): string {
    const totalSeconds = Math.floor(this.uptimeMs / 1000);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${h}h ${m}m ${s}s`;
  }

  private notifyAndroid(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const bridge = (window as any)['AndroidBridge'];
    if (bridge && typeof bridge.clearWebViewCache === 'function') {
      bridge.clearWebViewCache();
    }
  }

  /**
   * GC hint — allocates a large object immediately then discards it.
   * Encourages V8 to trigger a minor GC cycle.
   * Not guaranteed; behaviour is engine-dependent.
   */
  private gcHint(): void {
    try {
      const dummy = new Array(50_000).fill(null);
      dummy.length = 0;
    } catch {
      // Ignore allocation errors
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
